import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Bell, BellOff, Calendar, AlertTriangle, CheckCircle2, ListChecks, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import UniLogo from "@/components/app/UniLogo";
import { buildPrepTasks, taskDaysUntil } from "@/lib/prepTasks";

/**
 * DeadlineTracker
 * -----------------------------------------------------------------------------
 * Flattens each shortlisted degree's `application` block into a chronological
 * list of deadlines (opens / closes / decision / deposit), colour-coded by
 * urgency, and optionally fires browser notifications when a reminder
 * threshold is crossed.
 *
 * Reminders are stored per-degree in localStorage under the key `wua_reminders`
 * as a set of `${degree_id}::${deadline_type}` — presence in the set means
 * "user has opted in to be reminded 7 days before this deadline".
 */

const REMINDER_KEY = "wua_reminders_v1";
const LAST_FIRED_KEY = "wua_reminders_lastfired_v1";
const PREP_DONE_KEY = "wua_prep_done_v1";

function _readSet(key) {
  try { return new Set(JSON.parse(localStorage.getItem(key) || "[]")); } catch { return new Set(); }
}
function _writeSet(key, set) {
  try { localStorage.setItem(key, JSON.stringify([...set])); } catch {}
}

// Extract the first ISO-ish date (YYYY-MM-DD) from a deadline string.
// Handles values like "2027-01-15", "2027-03-14 (Parcoursup) / 2027-01-15 (DAP)".
function parseDate(str) {
  if (!str || typeof str !== "string") return null;
  const m = str.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const d = new Date(`${m[1]}-${m[2]}-${m[3]}T23:59:59Z`);
  return isNaN(d.getTime()) ? null : d;
}

function daysUntil(date) {
  if (!date) return null;
  const now = new Date();
  const ms = date.getTime() - now.getTime();
  return Math.round(ms / (1000 * 60 * 60 * 24));
}

const DEADLINE_TYPES = [
  { key: "opens", label: "Applications open", field: "opens" },
  { key: "closes", label: "Deadline (close)", field: "closes" },
  { key: "decision", label: "Decision by", field: "decision_by" },
  { key: "deposit", label: "Deposit due", field: "deposit_deadline" },
];

function urgencyChip(days) {
  if (days == null) return { cls: "bg-stone-100 text-stone-500 border-stone-200", label: "—" };
  if (days < 0) return { cls: "bg-stone-100 text-stone-500 border-stone-200 line-through", label: `${Math.abs(days)}d ago` };
  if (days <= 30) return { cls: "bg-red-50 text-red-800 border-red-200", label: `in ${days}d` };
  if (days <= 90) return { cls: "bg-amber-50 text-amber-900 border-amber-200", label: `in ${days}d` };
  return { cls: "bg-emerald-50 text-emerald-800 border-emerald-200", label: `in ${days}d` };
}

export default function DeadlineTracker({ items }) {
  const [reminders, setReminders] = useState(() => _readSet(REMINDER_KEY));
  const [permission, setPermission] = useState(() =>
    typeof Notification !== "undefined" ? Notification.permission : "denied"
  );
  const [prepDone, setPrepDone] = useState(() => _readSet(PREP_DONE_KEY));
  const [openPrep, setOpenPrep] = useState({});
  // Each per-course card starts COLLAPSED — the shortlist can grow to
  // 15-25 pinned courses and the raw expanded view (deadline rows +
  // full 12-item preparation checklist per course) crushes readability.
  // Header stays visible so the user can scan uni + next deadline; the
  // body opens on click.
  const [openGroups, setOpenGroups] = useState({});

  const togglePrepDone = (storageKey) => {
    setPrepDone((prev) => {
      const next = new Set(prev);
      if (next.has(storageKey)) next.delete(storageKey);
      else next.add(storageKey);
      _writeSet(PREP_DONE_KEY, next);
      return next;
    });
  };

  // Group flat deadline rows by degree so each course appears ONCE with all
  // its deadlines nested. Cards are sorted by the earliest still-upcoming
  // deadline (past-only degrees sink to the bottom).
  //
  // Every pinned DEGREE gets an entry — even if the university hasn't
  // published dated deadlines yet. Those cards render an empty-state
  // ("no published deadlines yet") so applicants can still see their
  // course in the tracker and don't wonder where their saved items went.
  // Uni-only pins (no degree_id) are excluded because there's no
  // course-level deadline concept to track for a whole university.
  const groups = useMemo(() => {
    const flat = [];
    for (const it of items || []) {
      const deg = it.degree;
      if (!deg) continue;   // uni-only pin — tracker is course-scoped
      const application = deg.application || {};
      const rows = [];
      for (const t of DEADLINE_TYPES) {
        const raw = application[t.field];
        const dt = parseDate(raw);
        if (!dt) continue;
        rows.push({
          key: `${deg.id}::${t.key}`,
          degree_id: deg.id,
          type: t.key,
          type_label: t.label,
          raw,
          date: dt,
          days: daysUntil(dt),
        });
      }
      // Fallback: many degrees carry a canonical top-level
      // `application_deadline` ISO string (populated by the per-
      // country deadline backfills) without a nested `application`
      // block. Without this branch those rows show "no published
      // deadlines yet" — surface them as the `closes` row instead.
      if (!rows.some((r) => r.type === "closes") && deg.application_deadline) {
        const raw = deg.application_deadline;
        const dt = parseDate(raw);
        if (dt) {
          rows.push({
            key: `${deg.id}::closes`,
            degree_id: deg.id,
            type: "closes",
            type_label: "Deadline (close)",
            raw,
            date: dt,
            days: daysUntil(dt),
          });
        }
      }
      rows.sort((a, b) => a.date - b.date);
      const upcoming = rows.filter((r) => r.days >= 0);
      const earliestUpcoming = upcoming.length > 0 ? upcoming[0].date : null;
      const prep = buildPrepTasks(deg).map((t) => ({
        ...t,
        days: taskDaysUntil(t.target_iso),
        storage_key: `${deg.id}::${t.key}`,
      }));
      flat.push({
        degree_id: deg.id,
        degree_title: deg.official_title,
        university_id: it.university?.id || it.university_id || "",
        university_name: it.university?.name || "",
        city: it.university?.city || "",
        country: it.university?.country || "",
        // Wikipedia-cached crest URL (populated by scripts/fetch_uni_logos.py).
        // Falls through UniLogo → Google favicon → initials tile if absent.
        logo_url: it.university?.logo_url || "",
        official_domain: (it.university?.official_domains || [])[0] || "",
        rows,
        prep,
        earliestUpcoming,
        hasNoDeadlines: rows.length === 0,
      });
    }
    return flat.sort((a, b) => {
      // Cards with upcoming deadlines float to the top, sorted by soonest.
      if (a.earliestUpcoming && !b.earliestUpcoming) return -1;
      if (!a.earliestUpcoming && b.earliestUpcoming) return 1;
      if (!a.earliestUpcoming && !b.earliestUpcoming) return 0;
      return a.earliestUpcoming - b.earliestUpcoming;
    });
  }, [items]);

  const allRows = useMemo(() => groups.flatMap((g) => g.rows), [groups]);

  const enableReminders = async () => {
    if (typeof Notification === "undefined") {
      toast.error("This browser doesn't support notifications.");
      return;
    }
    const result = await Notification.requestPermission();
    setPermission(result);
    if (result === "granted") toast.success("Browser reminders enabled. We'll ping you 7 days before each deadline.");
    else toast.info("Reminders blocked — you can still see deadlines here.");
  };

  const toggleReminder = (key) => {
    setReminders((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      _writeSet(REMINDER_KEY, next);
      return next;
    });
  };

  // Fire notifications for any reminders in the 7d window that haven't been
  // fired today. Cheap: runs once on mount + every 5 minutes.
  useEffect(() => {
    if (permission !== "granted") return;
    const fired = _readSet(LAST_FIRED_KEY);
    const today = new Date().toISOString().slice(0, 10);
    const check = () => {
      for (const r of allRows) {
        if (!reminders.has(r.key)) continue;
        if (r.days == null || r.days < 0 || r.days > 7) continue;
        const marker = `${r.key}::${today}`;
        if (fired.has(marker)) continue;
        try {
          new Notification("Deadline coming up", {
            body: `${r.type_label} in ${r.days} day(s).`,
            icon: "/favicon.ico",
            tag: r.key,
          });
          fired.add(marker);
          _writeSet(LAST_FIRED_KEY, fired);
        } catch {}
      }
    };
    check();
    const t = setInterval(check, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [reminders, permission, allRows]);

  const withDegreeCount = (items || []).filter((it) => it.degree_id).length;
  const withDatesCount = groups.filter((g) => !g.hasNoDeadlines).length;
  const withoutDatesCount = groups.length - withDatesCount;

  if ((items || []).length === 0) return null;

  return (
    <Card className="mt-8 p-6 border-stone-200" data-testid="deadline-tracker">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-uppercase inline-flex items-center gap-2">
            <Calendar className="h-3.5 w-3.5" /> Deadline tracker
            {groups.length > 0 && withoutDatesCount > 0 && (
              <span
                data-testid="deadline-tracker-coverage"
                className="ml-2 normal-case tracking-normal text-[11px] text-stone-500"
              >
                {withDatesCount} of {groups.length} degree{groups.length === 1 ? "" : "s"} have published deadlines
              </span>
            )}
          </div>
          <p className="text-sm text-stone-600 mt-1 max-w-2xl">
            {groups.length > 0
              ? "One card per course with dated deadlines AND a preparation checklist — every task gets a targeted date so you know when to start, not just when to finish. Toggle a bell to get a browser reminder 7 days before that date."
              : withDegreeCount === 0
                ? "Your shortlist is uni-level. Open a specific degree from the Explore page and press \"Shortlist this degree\" to unlock deadline tracking."
                : "No dated deadlines available for the degrees you've saved."}
          </p>
        </div>
        {groups.length > 0 && (
          permission === "granted" ? (
            <div className="inline-flex items-center gap-1.5 text-xs text-emerald-700" data-testid="reminders-status">
              <CheckCircle2 className="h-3.5 w-3.5" /> Browser reminders enabled
            </div>
          ) : (
            <Button variant="outline" size="sm" onClick={enableReminders} data-testid="enable-reminders-btn">
              <Bell className="h-3.5 w-3.5 mr-1.5" /> Enable browser reminders
            </Button>
          )
        )}
      </div>

      {groups.length > 0 && (
        <div className="mt-6 space-y-4">
          {groups.map((g) => {
            const nextRow = g.rows.find((r) => r.days >= 0);
            const groupOpen = !!openGroups[g.degree_id];
            const toggleGroup = () =>
              setOpenGroups((s) => ({ ...s, [g.degree_id]: !s[g.degree_id] }));
            return (
              <div key={g.degree_id}
                   data-testid={`deadline-group-${g.degree_id}`}
                   className={`border rounded-lg overflow-hidden bg-white ${
                     g.hasNoDeadlines ? "border-stone-200/80 opacity-90" : "border-stone-200"
                   }`}>
                <button
                  type="button"
                  onClick={toggleGroup}
                  aria-expanded={groupOpen}
                  data-testid={`deadline-group-toggle-${g.degree_id}`}
                  className={`w-full flex items-center justify-between gap-3 px-4 py-3 bg-stone-50 text-left hover:bg-stone-100/70 transition-colors ${
                    groupOpen ? "border-b border-stone-100" : ""
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <ChevronDown
                      className={`h-4 w-4 shrink-0 text-stone-500 transition-transform ${
                        groupOpen ? "rotate-0" : "-rotate-90"
                      }`}
                      aria-hidden
                    />
                    <UniLogo
                      uniId={g.university_id}
                      uniName={g.university_name}
                      logoUrl={g.logo_url}
                      officialDomain={g.official_domain}
                      country={g.country}
                      size="md"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="font-editorial text-base truncate">{g.degree_title}</div>
                      <div className="text-[11px] text-stone-500 truncate">
                        {g.university_name}{g.city ? ` · ${g.city}` : ""}
                      </div>
                    </div>
                  </div>
                  {g.hasNoDeadlines ? (
                    <span
                      data-testid={`deadline-status-nodates-${g.degree_id}`}
                      className="text-[10px] font-medium border border-stone-200 rounded-full px-2 py-0.5 bg-stone-100 text-stone-500 whitespace-nowrap"
                    >
                      no dates published
                    </span>
                  ) : nextRow ? (
                    <span className="text-[10px] font-medium border border-stone-300 rounded-full px-2 py-0.5 bg-white text-stone-700 whitespace-nowrap">
                      next: {nextRow.type_label.toLowerCase()} · {urgencyChip(nextRow.days).label}
                    </span>
                  ) : (
                    <span className="text-[10px] font-medium border border-stone-200 rounded-full px-2 py-0.5 bg-stone-100 text-stone-400 whitespace-nowrap">
                      all past
                    </span>
                  )}
                </button>
                {groupOpen && (
                  <>
                {g.hasNoDeadlines ? (
                  <div
                    data-testid={`deadline-empty-${g.degree_id}`}
                    className="px-4 py-4 text-[13px] text-stone-600 bg-white"
                  >
                    <div className="font-medium text-stone-800 mb-1">
                      No published deadlines for this course yet.
                    </div>
                    <p className="text-[12px] text-stone-600 leading-snug">
                      The university hasn't posted dated milestones (applications-open,
                      deadline, decision-by, deposit-due) that we can verify. The preparation
                      checklist below still applies — we'll auto-add dates as soon as the
                      admissions page publishes them.
                    </p>
                  </div>
                ) : (
                <div className="divide-y divide-stone-100">
                  {g.rows.map((r) => {
                    const chip = urgencyChip(r.days);
                    const on = reminders.has(r.key);
                    return (
                      <div key={r.key}
                           data-testid={`deadline-row-${r.degree_id}-${r.type}`}
                           className="flex items-center justify-between px-4 py-2.5 gap-4">
                        <div className="min-w-0 flex-1 text-[11px] text-stone-600 truncate">
                          <span className="uppercase tracking-wider text-stone-800 font-medium">{r.type_label}</span>
                          <span className="mx-1.5">·</span>
                          <span className="font-mono">{r.raw}</span>
                        </div>
                        <span className={`text-[10px] font-medium border rounded-full px-2 py-0.5 shrink-0 ${chip.cls}`}
                              data-testid={`deadline-chip-${r.degree_id}-${r.type}`}>
                          {chip.label}
                        </span>
                        <Button
                          variant="ghost" size="sm"
                          data-testid={`reminder-toggle-${r.degree_id}-${r.type}`}
                          onClick={() => toggleReminder(r.key)}
                          className={`shrink-0 ${on ? "text-[#4C1D95]" : "text-stone-400"}`}
                          title={on ? "Remove reminder" : "Add reminder"}>
                          {on ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}
                        </Button>
                      </div>
                    );
                  })}
                </div>
                )}

                {/* PREP CHECKLIST — data-driven from the degree's own attributes.
                    Collapsible so the tracker stays scannable at rest. */}
                {g.prep.length > 0 && (() => {
                  const isOpen = openPrep[g.degree_id] ?? true;
                  const doneCount = g.prep.filter((t) => prepDone.has(t.storage_key)).length;
                  const total = g.prep.length;
                  const phases = ["Before application", "Application week", "After application"];
                  return (
                    <div
                      data-testid={`prep-checklist-${g.degree_id}`}
                      className="border-t border-stone-100 bg-stone-50/50"
                    >
                      <button
                        type="button"
                        onClick={() => setOpenPrep((s) => ({ ...s, [g.degree_id]: !isOpen }))}
                        data-testid={`prep-toggle-${g.degree_id}`}
                        className="w-full flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-stone-100/60 transition-colors"
                        aria-expanded={isOpen}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <ListChecks className="h-3.5 w-3.5 text-[#4C1D95] shrink-0" />
                          <span className="uppercase tracking-wider text-[11px] font-medium text-stone-800">
                            Preparation checklist
                          </span>
                          <span
                            data-testid={`prep-progress-${g.degree_id}`}
                            className="text-[10px] font-mono text-stone-500"
                          >
                            {doneCount}/{total} done
                          </span>
                          <div className="ml-1 hidden sm:block w-24 h-1.5 rounded-full bg-stone-200 overflow-hidden">
                            <div
                              className="h-full bg-[#4C1D95] transition-all"
                              style={{ width: `${total ? (doneCount / total) * 100 : 0}%` }}
                            />
                          </div>
                        </div>
                        <ChevronDown
                          className={`h-4 w-4 text-stone-500 transition-transform ${isOpen ? "" : "-rotate-90"}`}
                        />
                      </button>

                      {isOpen && (
                        <div className="px-4 pb-4 pt-1 space-y-4">
                          {phases.map((phase) => {
                            const rows = g.prep.filter((t) => t.phase === phase);
                            if (rows.length === 0) return null;
                            return (
                              <div key={phase}>
                                <div className="label-uppercase !text-[10px] text-stone-500 mb-2">{phase}</div>
                                <div className="space-y-2">
                                  {rows.map((t) => {
                                    const done = prepDone.has(t.storage_key);
                                    const chip = urgencyChip(t.days);
                                    const isDeadline = t.target_kind === "deadline";
                                    return (
                                      <div
                                        key={t.storage_key}
                                        data-testid={`prep-task-${g.degree_id}-${t.key}`}
                                        className={`flex items-start gap-3 rounded-md border px-3 py-2.5 bg-white ${
                                          done ? "border-stone-200 opacity-60" : "border-stone-200"
                                        }`}
                                      >
                                        <Checkbox
                                          checked={done}
                                          onCheckedChange={() => togglePrepDone(t.storage_key)}
                                          data-testid={`prep-task-check-${g.degree_id}-${t.key}`}
                                          className="mt-0.5 shrink-0"
                                        />
                                        <div className="min-w-0 flex-1">
                                          <div className="flex items-baseline gap-2 flex-wrap">
                                            <div className={`text-sm text-stone-900 ${done ? "line-through" : "font-medium"}`}>
                                              {t.label}
                                            </div>
                                            {t.international_only && (
                                              <span className="text-[9px] font-underground-sc tracking-wider text-stone-500 border border-stone-300 rounded-full px-1.5 py-0.5">
                                                intl
                                              </span>
                                            )}
                                          </div>
                                          <p className="text-[12px] text-stone-600 mt-1 leading-snug">
                                            {t.description}
                                          </p>
                                          {t.target_iso && (
                                            <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                                              <span className="text-[10px] font-mono text-stone-500">
                                                {isDeadline ? "hard deadline" : "target"} · {t.target_iso}
                                              </span>
                                              <span
                                                className={`text-[10px] font-medium border rounded-full px-2 py-0.5 ${chip.cls}`}
                                                data-testid={`prep-task-chip-${g.degree_id}-${t.key}`}
                                              >
                                                {chip.label}
                                              </span>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })()}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {groups.length > 0 && reminders.size > 0 && permission !== "granted" && (
        <div className="mt-4 inline-flex items-center gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2"
             data-testid="reminders-blocked-warn">
          <AlertTriangle className="h-3.5 w-3.5" />
          You have {reminders.size} reminder(s) set but browser notifications are blocked — enable them to receive alerts.
        </div>
      )}
    </Card>
  );
}
